import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  getRouterSelectors,
  SerializedRouterStateSnapshot,
} from '@ngrx/router-store';
import {
  createActionGroup,
  props,
  emptyProps,
  createFeature,
  createReducer,
  createSelector,
  on,
} from '@ngrx/store';
import { catchError, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import { StringLiteralCheck } from 'src/app/shared/util/string-literal-check';

// Configurable query param names
export interface EditItemQueryParamConfig {
  edit: string;
}

export const DEFAULT_DRILLDOWN_QUERY_PARAMS_CONFIG: EditItemQueryParamConfig = {
  edit: 'edit',
};

export interface EditItemFeatureConfig {
  routeFilter: (route: SerializedRouterStateSnapshot) => boolean;
  queryParamsConfig?: EditItemQueryParamConfig;
}

export interface EditItemEffectConfig<TData, TEdit> {
  saveItem: (originalItem: TData, edit: TEdit) => Observable<TData>;
  notifyFail?: (originalItem: TData, edit: TEdit, error: unknown) => void;
  confirmCancel?: () => Observable<boolean>;
}

/**
 * @param featureName Literal string to be used as the feature name and actions source
 * @param config Required configuration: router and filtering.
 * @returns NGRX feature, actions, and effect builder.
 *
 * The NGRX feature implements URL-driven data flow for editing.  The object to
 * be edited is identified by a string key, which becomes the value of the query
 * parameter "edit".
 *
 * The component receives the edit key via the `selectEditItemKey` selector.
 * The edit key is either a string identifying the object for editing, or `null`
 * meaning that editing is not requested.
 *
 * The following actions are supported:
 *   edit(key): request editing for the object identified with the string `key`
 *   save(editedObject): save request
 *   saveSuccess(editedObject): on successful saving
 *   saveFail(editedObject, error): on failed saving
 *   cancel({ confirm }): cancel with optional confirmation
 *   doCancel(): perform canceling
 * Normally, `edit` is dispatched by the master component to initiate the
 * editing, and `save` or `cancel` from the editor component.
 *
 * The effect builder is a function returning effects that should be embedded in
 * page's effects.  Internally, the effects perform routing tasks.
 */
export function createEditItemFeature<
  TActionSource extends string & StringLiteralCheck<TActionSource, 'source'>,
  TData,
  TEdit,
>(featureName: TActionSource, config: EditItemFeatureConfig) {
  const queryParamsConfig =
    config.queryParamsConfig ?? DEFAULT_DRILLDOWN_QUERY_PARAMS_CONFIG;
  const actions = createActionGroup({
    source: featureName,
    events: {
      edit: props<{ key: string }>(),
      save: props<{ originalObject: TData; edit: TEdit }>(),
      saveSuccess: props<{ editedObject: TData }>(),
      saveFail: props<{ originalObject: TData; edit: TEdit; error: unknown }>(),
      cancel: props<{ confirmation: boolean }>(),
      doCancel: emptyProps(),
    },
  });
  const feature = createFeature({
    name: featureName,
    reducer: createReducer(
      { saving: false },
      on(actions.save, () => ({ saving: true })),
      on(actions.saveSuccess, () => ({ saving: false })),
      on(actions.saveFail, () => ({ saving: false })),
    ),
    extraSelectors: () => {
      const { selectQueryParams } = getRouterSelectors();
      const selectEditItemKey = createSelector(
        selectQueryParams,
        queryParams => queryParams?.[queryParamsConfig.edit],
      );
      return { selectEditItemKey };
    },
  });
  const createEffects = (
    action$: Actions,
    router: Router,
    config: EditItemEffectConfig<TData, TEdit>,
  ) => {
    const { saveItem, notifyFail, confirmCancel } = config;
    return {
      edit$: createEffect(
        () =>
          action$.pipe(
            ofType(actions.edit),
            tap(action => {
              const queryParams = { [queryParamsConfig.edit]: action.key };
              router.navigate([], {
                queryParams,
                queryParamsHandling: 'merge',
              });
            }),
          ),
        { dispatch: false },
      ),
      save$: createEffect(() =>
        action$.pipe(
          ofType(actions.save),
          switchMap(action =>
            saveItem(action.originalObject, action.edit).pipe(
              map(editedObject => actions.saveSuccess({ editedObject })),
              catchError(error => {
                console.error('Error saving edited object', error);
                return of(
                  actions.saveFail({
                    originalObject: action.originalObject,
                    edit: action.edit,
                    error,
                  }),
                );
              }),
            ),
          ),
        ),
      ),

      saveSuccess$: createEffect(
        () =>
          action$.pipe(
            ofType(actions.saveSuccess),
            tap(() => {
              const queryParams = { [queryParamsConfig.edit]: null };
              router.navigate([], {
                queryParams,
                queryParamsHandling: 'merge',
              });
            }),
          ),
        { dispatch: false },
      ),

      saveFail$: createEffect(
        () =>
          action$.pipe(
            ofType(actions.saveFail),
            tap(action =>
              notifyFail?.(action.originalObject, action.edit, action.error),
            ),
          ),
        { dispatch: false },
      ),

      cancel$: createEffect(() =>
        action$.pipe(
          ofType(actions.cancel),
          switchMap(action =>
            action.confirmation && confirmCancel ? confirmCancel() : of(true),
          ),
          filter(confirmed => confirmed),
          map(() => actions.doCancel()),
        ),
      ),

      doCancel$: createEffect(
        () =>
          action$.pipe(
            ofType(actions.doCancel),
            tap(() => {
              const queryParams = { [queryParamsConfig.edit]: null };
              router.navigate([], {
                queryParams,
                queryParamsHandling: 'merge',
              });
            }),
          ),
        { dispatch: false },
      ),
    };
  };
  return { feature, actions, createEffects };
}
